import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { ArrowLeft, Download, Search, RefreshCw } from "lucide-react";
import * as XLSX from "xlsx";
import { getMonthlyFaxReport } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import "./MonthlyFaxDetailsReport.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()} ${hh}:${mi}`;
};

const MonthlyFaxDetailsReport = ({
  domain,
  companyId,
  account,
  source,
  fromDate,
  toDate,
  onBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const loadPage = async (page, size, search = appliedSearch) => {
    if (!domain || !fromDate || !toDate || !companyId) {
      toast.error("Missing filters. Go back and search again.");
      return;
    }
    setLoading(true);
    try {
      const res = await getMonthlyFaxReport({
        domain,
        companyId,
        fromDate,
        toDate,
        source: source || "",
        searchText: search || "",
        tabName: "Details",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.items ? res : res?.data || {};
      setRows(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
      setAppliedSearch(search || "");
      if (!(data?.items || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, pageSize, "");
  }, [domain, companyId, fromDate, toDate, source]);

  const handleSearch = () => loadPage(1, pageSize, searchText.trim());

  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await getMonthlyFaxReport({
        domain,
        companyId,
        fromDate,
        toDate,
        source: source || "",
        searchText: appliedSearch || "",
        tabName: "Details",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const data = res?.items ? res : res?.data || {};
      const list = data?.items || [];
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      if ((data.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${data.totalCount}`);
      }
      const mapped = list.map((r) => ({
        Account: r.account,
        CompanyId: r.companyId,
        "Patient Name [Case#]": r.patientNameCase,
        "Created By": r.createdBy,
        "Fax# From": r.faxFrom,
        "Fax# To": r.faxTo,
        SenderName: r.senderName,
        FaxRecipientName: r.faxRecipientName,
        TotalFaxedPages: r.totalFaxedPages,
        "Faxed Date": formatDate(r.faxedDate),
        "Fax Ack Date": formatDate(r.faxAckDate),
        Source: r.source,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "MonthlyFaxReport_Details.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mfd-page">
      <Toaster position="top-right" />

      <div className="mfd-header">
        <button type="button" className="mfd-btn mfd-btn-back" onClick={onBack}>
          <ArrowLeft size={14} />
          Back
        </button>

        <div className="mfd-title">
          <h3>
            {account || "Company"} ({companyId})
          </h3>
          <span>
            {fromDate} → {toDate}
            {source ? ` · ${source}` : " · All sources"}
          </span>
        </div>        

        <div className="mfd-search-row">
          <input
            className="mfd-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search Fax# To, Fax Recipient Name"
          />
          <button type="button" className="mfd-btn mfd-btn-back" onClick={handleSearch} disabled={loading}>
            <Search size={14} />
            Search
          </button>
          <button
            type="button"
            className="mfd-btn mfd-btn-reset"
            onClick={() => {
              setSearchText("");
              loadPage(1, pageSize, "");
            }}
            disabled={loading}
          >
            <RefreshCw size={14} />
            Reset
          </button>
        </div>

        <button
          type="button"
          className="mfd-btn mfd-btn-reset"
          onClick={handleDownload}
          disabled={loading || !rows.length}
        >
          <Download size={14} />
          Excel
        </button>
      </div>

      <div className="mfd-table-wrap">
        <table className="mfd-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Patient Name [Case#]</th>
              <th>Created By</th>
              <th>Fax# From</th>
              <th>Fax# To</th>
              <th>Sender Name</th>
              <th>Fax Recipient Name</th>
              <th>Total Faxed Pages</th>
              <th>Faxed Date</th>
              <th>Fax Ack Date</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="mfd-empty">
                  {loading ? "Loading..." : "No data"}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.companyId}|${r.faxedDate}|${i}`}>
                  <td>
                    {r.account} - {r.companyId}
                  </td>
                  <td>{r.patientNameCase}</td>
                  <td>{r.createdBy}</td>
                  <td>{r.faxFrom}</td>
                  <td>{r.faxTo}</td>
                  <td>{r.senderName}</td>
                  <td>{r.faxRecipientName}</td>
                  <td>{r.totalFaxedPages}</td>
                  <td>{formatDate(r.faxedDate)}</td>
                  <td>{formatDate(r.faxAckDate)}</td>
                  <td>{r.source}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="mfd-pager">
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((o) => o.value === pageSize)}
            onChange={(opt) => loadPage(1, opt.value, appliedSearch)}
            styles={selectStyles}
            menuPlacement="top"
            isSearchable={false}
          />
          <span>
            {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
          </span>
          <button
            type="button"
            className="mfd-btn mfd-btn-reset"
            disabled={safePage <= 1}
            onClick={() => loadPage(safePage - 1, pageSize, appliedSearch)}
          >
            Prev
          </button>
          <button
            type="button"
            className="mfd-btn mfd-btn-reset"
            disabled={safePage >= totalPages}
            onClick={() => loadPage(safePage + 1, pageSize, appliedSearch)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MonthlyFaxDetailsReport;